import { useEffect, useState, type ReactElement } from "react";
import { SOLVE_PHASES, type SolvePhase } from "@playbookai/types";
import { widgetsMap } from "@playbookai/collab-client";
import type * as Y from "yjs";
import { LANE_WIDTH, PHASE_META } from "./phase-metadata.js";
import {
  CanvasMiniMap,
  stickyNotesMap,
  type CanvasMiniMapLayer,
  type Rect,
} from "@playbookai/ui";
import {
  defaultPhaseAreaRect,
  phaseAreaExtent,
  type PhaseAreaLayout,
} from "./phase-areas.js";

const MINI_W = 260;
const MINI_LANE_W = MINI_W / SOLVE_PHASES.length;
// The scale stays FIXED to the historical five-lane board rather than being
// re-fitted to the live extent. A mini-map that rescaled on every resize would
// make the resize itself invisible — everything would keep its on-screen size —
// and would jitter the whole overview mid-gesture. The BOX grows instead, which
// is exactly what the revealed-prefix width already did.
//
// Passing `width`/`height` computed from the LIVE extent at this fixed scale,
// with `frame` set to that same extent, makes CanvasMiniMap's own contain-fit
// recompute exactly this scale (uniform on both axes) — so the box keeps
// growing with the extent instead of CanvasMiniMap rescaling it.
const SCALE = MINI_LANE_W / LANE_WIDTH;

export function SolveMiniMap({
  doc,
  phases = SOLVE_PHASES,
  areas,
  resolveWidgetVisibility,
  resolveNoteVisibility,
}: {
  readonly doc: Y.Doc;
  readonly phases?: readonly SolvePhase[];
  /** Live rect per phase. Absent falls back to the historical fixed lanes. */
  readonly areas?: PhaseAreaLayout | undefined;
  readonly resolveWidgetVisibility?:
    | ((ymap: Y.Map<unknown>) => boolean)
    | undefined;
  readonly resolveNoteVisibility?:
    | ((ymap: Y.Map<unknown>) => boolean)
    | undefined;
}): ReactElement {
  const wmap = widgetsMap(doc);
  const notes = stickyNotesMap(doc);
  const [, force] = useState(0);

  useEffect(() => {
    const observe = (): void => force((n) => n + 1);
    wmap.observeDeep(observe);
    notes.observeDeep(observe);
    return (): void => {
      wmap.unobserveDeep(observe);
      notes.unobserveDeep(observe);
    };
  }, [notes, wmap]);

  const widgets: Y.Map<unknown>[] = [];
  wmap.forEach((ymap) => {
    if (resolveWidgetVisibility?.(ymap) === false) return;
    widgets.push(ymap);
  });
  const stickyNotes: Y.Map<unknown>[] = [];
  notes.forEach((note) => {
    if (resolveNoteVisibility?.(note) === false) return;
    stickyNotes.push(note);
  });

  const rects = new Map<SolvePhase, Rect>(
    phases.map((phase): [SolvePhase, Rect] => [
      phase,
      areas?.get(phase) ?? defaultPhaseAreaRect(phase),
    ]),
  );
  const { w: extentW, h: extentH } = phaseAreaExtent(rects);
  const frame: Rect = { x: 0, y: 0, w: extentW, h: extentH };

  const layers: CanvasMiniMapLayer[] = [
    { id: "widgets", kind: "item", rects: widgets.map(ymapRect) },
    { id: "notes", kind: "note", rects: stickyNotes.map(ymapRect) },
  ];

  return (
    <CanvasMiniMap
      data-testid="solve-minimap"
      className="absolute bottom-20 start-4 z-10"
      surface="glass"
      shape="panel"
      frame={frame}
      width={extentW * SCALE}
      height={extentH * SCALE}
      layers={layers}
      renderUnderlay={(project) =>
        phases.map((phase) => {
          const rect = rects.get(phase) ?? defaultPhaseAreaRect(phase);
          const box = project(rect);
          return (
            <div
              key={phase}
              className="absolute top-0 border-r border-pb-border/70"
              style={{
                left: box.x,
                width: box.w,
                height: box.h,
                background: PHASE_META[phase].accentBg,
              }}
            >
              <span className="absolute start-1 top-1 text-[8px] font-bold text-pb-text-muted">
                {PHASE_META[phase].letter}
              </span>
            </div>
          );
        })
      }
    />
  );
}

function ymapRect(ymap: Y.Map<unknown>): Rect {
  return {
    x: miniNumber(ymap, "x", 0),
    y: miniNumber(ymap, "y", 0),
    w: miniNumber(ymap, "w", 180),
    h: miniNumber(ymap, "h", 120),
  };
}

function miniNumber(
  ymap: Y.Map<unknown>,
  key: string,
  fallback: number,
): number {
  const value = ymap.get(key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
